import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "config";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

const TERMS_URLS = {
  gr: process.env.REACT_APP_TERMS_URL_GR || "",
  en: process.env.REACT_APP_TERMS_URL_EN || "",
};

const PRIVACY_URLS = {
  gr: process.env.REACT_APP_PRIVACY_URL_GR || "",
  en: process.env.REACT_APP_PRIVACY_URL_EN || "",
};

export default function RegisterAffiliateForm({ planKey, categoryId, selectedCategory, plan, onBack, initialPromoCode, initialCity }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const categoryLabel = selectedCategory
    ? selectedCategory[lang] || selectedCategory.gr
    : categoryId;

  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState(initialCity || "");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [websiteOffer, setWebsiteOffer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);

  // Promo code state
  const [promoInput, setPromoInput] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null); // { type, percentOff?, trialDays?, code }
  const [promoErr, setPromoErr] = useState("");

  useEffect(() => {
    fetch(`${config.BACKEND_BASE_URL}/api/filters/cities`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setCities(list);
        // Pre-select only when a valid deep-link city is provided
        if (initialCity) {
          const match = list.find((c) => c.nameEn === initialCity);
          if (match) setCity(initialCity);
        }
      })
      .catch(() => {})
      .finally(() => setCitiesLoading(false));
  }, []);

  // Auto-apply promo code from deep link
  useEffect(() => {
    if (!initialPromoCode) return;
    const code = initialPromoCode.trim().toUpperCase();
    setPromoLoading(true);
    axios
      .post(`${config.BACKEND_BASE_URL}/api/admin/register/promo/validate`, { code })
      .then((res) => setAppliedPromo({ ...res.data, code }))
      .catch(() => {}) // silently ignore invalid deep-link promo
      .finally(() => setPromoLoading(false));
  }, [initialPromoCode]);

  const PLANS = {
    y1: t("register.form.plans.y1"),
    y3: t("register.form.plans.y3"),
    y1p: t("register.form.plans.y1p"),
    website: t("register.form.plans.website"),
  };

  // Compute effective display price
  const baseAmount = plan?.amount ?? 0;
  const discountedAmount =
    appliedPromo?.type === "discount" && appliedPromo.percentOff
      ? Math.round(baseAmount * (1 - appliedPromo.percentOff / 100))
      : null;

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const emailError = emailTouched && !emailValid;

  const canSubmit =
    !loading &&
    emailValid &&
    phone.trim() &&
    businessName.trim() &&
    city &&
    termsAccepted;

  const handleApplyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoErr("");
    setAppliedPromo(null);
    setPromoLoading(true);
    try {
      const res = await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/register/promo/validate`,
        { code }
      );
      setAppliedPromo({ ...res.data, code });
      setPromoInput("");
    } catch (e) {
      setPromoErr(e?.response?.data?.message || t("register.form.promo.invalid"));
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoErr("");
  };

  const handleSubmit = async () => {
    setErrMsg("");
    setLoading(true);
    try {
      const startRes = await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/register/onboarding/start`,
        {
          planKey,
          email,
          phone,
          businessName,
          city,
          categoryId,
          websiteOffer,
          promoCode: appliedPromo?.code ?? undefined,
        }
      );

      const { pendingAffiliateId } = startRes.data;

      const checkoutRes = await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/register/stripe/checkout-session`,
        { pendingAffiliateId }
      );

      window.location.href = checkoutRes.data.checkoutUrl;
    } catch (e) {
      setErrMsg(e?.response?.data?.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const periodLabel = plan
    ? plan.intervalCount === 1
      ? t("pricing.periodSingle")
      : t("pricing.periodMultiple", { count: plan.intervalCount })
    : "";

  const cityLabel = (c) => (lang === "gr" ? c.nameGr : c.nameEn);

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={10} lg={7}>
        {/* Price summary */}
        <Card sx={{ mb: 3, overflow: "hidden" }}>
          <MDBox
            variant="gradient"
            bgColor="info"
            py={2.5}
            px={3}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <MDBox>
              <MDTypography variant="button" color="white" fontWeight="bold" display="block">
                {PLANS[planKey] || planKey}
              </MDTypography>
              <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                {categoryLabel}
              </MDTypography>
            </MDBox>
            {plan && (
              <MDBox textAlign="right">
                {discountedAmount !== null ? (
                  <>
                    <MDTypography
                      variant="caption"
                      color="white"
                      sx={{ opacity: 0.65, textDecoration: "line-through", display: "block" }}
                    >
                      €{baseAmount}
                    </MDTypography>
                    <MDTypography variant="h5" color="white" fontWeight="bold">
                      €{discountedAmount}
                      <MDTypography component="span" variant="caption" color="white" sx={{ opacity: 0.85, ml: 0.5 }}>
                        {periodLabel}
                      </MDTypography>
                    </MDTypography>
                  </>
                ) : (
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    {appliedPromo?.type === "trial"
                      ? t("register.form.promo.trialPrice")
                      : `€${baseAmount}`}
                    <MDTypography component="span" variant="caption" color="white" sx={{ opacity: 0.85, ml: 0.5 }}>
                      {periodLabel}
                    </MDTypography>
                  </MDTypography>
                )}
              </MDBox>
            )}
          </MDBox>

          {/* Applied promo banner */}
          {appliedPromo && (
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={1}
              sx={{ backgroundColor: "success.main" }}
            >
              <MDBox display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#fff" }}>
                  {appliedPromo.type === "discount"
                    ? t("register.form.promo.appliedDiscount", { pct: appliedPromo.percentOff })
                    : t("register.form.promo.appliedTrial", { days: appliedPromo.trialDays })}
                </MDTypography>
              </MDBox>
              <MDTypography
                variant="caption"
                sx={{ color: "#fff", cursor: "pointer", textDecoration: "underline" }}
                onClick={handleRemovePromo}
              >
                {t("register.form.promo.remove")}
              </MDTypography>
            </MDBox>
          )}
        </Card>

        {/* Form */}
        <Card>
          <MDBox p={4}>
            <MDBox mb={3}>
              <MDTypography variant="h5" fontWeight="bold">
                {t("register.form.title")}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {t("register.form.subtitle")}
              </MDTypography>
            </MDBox>

            <MDBox component="form" role="form" onSubmit={(e) => e.preventDefault()}>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label={t("register.form.email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  error={emailError}
                  helperText={emailError ? t("register.form.emailInvalid") : ""}
                  fullWidth
                />
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  type="tel"
                  label={t("register.form.phone")}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                />
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  label={t("register.form.businessName")}
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  fullWidth
                />
              </MDBox>

              <MDBox mb={3}>
                {(() => {
                  const cityOptions = citiesLoading
                    ? []
                    : [
                        ...cities.map((c) => ({ value: c.nameEn, label: cityLabel(c) })),
                        { value: "Other", label: t("register.form.cityOther") },
                      ];
                  const selectedOption = cityOptions.find((o) => o.value === city) || null;
                  return (
                    <Autocomplete
                      options={cityOptions}
                      getOptionLabel={(o) => o.label}
                      value={selectedOption}
                      onChange={(_, newValue) => setCity(newValue?.value || "")}
                      loading={citiesLoading}
                      isOptionEqualToValue={(o, v) => o.value === v.value}
                      sx={{ "& .MuiAutocomplete-option": { padding: "16px 20px !important" } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("register.form.city")}
                          sx={{ "& .MuiInputBase-input": { padding: "2px 4px !important" } }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {citiesLoading && <CircularProgress size={16} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  );
                })()}
                {city === "Other" && (
                  <MDBox
                    display="flex"
                    alignItems="flex-start"
                    gap={1}
                    mt={1.5}
                    p={1.5}
                    sx={{ backgroundColor: "info.main", borderRadius: 2 }}
                  >
                    <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 18, flexShrink: 0, mt: "1px" }} />
                    <MDTypography variant="caption" sx={{ color: "#fff", lineHeight: 1.5 }}>
                      {t("register.form.cityOtherInfo")}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>

              <Divider />

              {/* Promo code */}
              {!appliedPromo && (
                <MDBox mb={3}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" display="block" mb={1}>
                    <CardGiftcardIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                    {t("register.form.promo.label")}
                  </MDTypography>
                  <MDBox display="flex" gap={1}>
                    <MDInput
                      label={t("register.form.promo.placeholder")}
                      value={promoInput}
                      onChange={(e) => {
                        setPromoInput(e.target.value.toUpperCase());
                        setPromoErr("");
                      }}
                      onKeyDown={(e) => e.key === "Enter" && handleApplyPromo()}
                      fullWidth
                    />
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoInput.trim()}
                      sx={{ flexShrink: 0 }}
                    >
                      {promoLoading ? <CircularProgress size={18} /> : t("register.form.promo.apply")}
                    </MDButton>
                  </MDBox>
                  {promoErr && (
                    <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                      {promoErr}
                    </MDTypography>
                  )}
                </MDBox>
              )}

              <Divider />

              {/* Terms & Conditions */}
              <MDBox mb={1.5} display="flex" alignItems="flex-start" gap={1}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{
                    marginTop: 3,
                    flexShrink: 0,
                    cursor: "pointer",
                    accentColor: "#1a73e8",
                    width: 15,
                    height: 15,
                  }}
                />
                <MDTypography variant="caption" color="text" sx={{ lineHeight: 1.5 }}>
                  {t("register.form.termsText")}{" "}
                  <MDTypography
                    component="a"
                    href={TERMS_URLS[lang] || TERMS_URLS.en}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    fontWeight="medium"
                    sx={{ color: "info.main", cursor: "pointer" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("register.form.termsLink")}
                  </MDTypography>
                  .
                </MDTypography>
              </MDBox>

              {/* Website offer */}
              <MDBox mb={3} display="flex" alignItems="flex-start" gap={1}>
                <input
                  type="checkbox"
                  checked={websiteOffer}
                  onChange={(e) => setWebsiteOffer(e.target.checked)}
                  style={{
                    marginTop: 3,
                    flexShrink: 0,
                    cursor: "pointer",
                    accentColor: "#1a73e8",
                    width: 15,
                    height: 15,
                  }}
                />
                <MDTypography variant="caption" color="text" sx={{ lineHeight: 1.5 }}>
                  {t("register.form.websiteOfferText")}
                </MDTypography>
              </MDBox>

              {/* Privacy Policy */}
              <MDBox mb={1.5}>
                <MDTypography variant="caption" color="text" sx={{ lineHeight: 1.5 }}>
                  {t("register.form.privacyText")}{" "}
                  <MDTypography
                    component="a"
                    href={PRIVACY_URLS[lang] || PRIVACY_URLS.en}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    fontWeight="medium"
                    sx={{ color: "info.main", cursor: "pointer" }}
                  >
                    {t("register.form.privacyLink")}
                  </MDTypography>
                  .
                </MDTypography>
              </MDBox>

              {errMsg ? (
                <MDBox mb={2}>
                  <MDTypography variant="button" color="error">
                    {errMsg}
                  </MDTypography>
                </MDBox>
              ) : null}

              <MDBox mt={1} display="flex" gap={1.5}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {loading ? t("register.form.redirecting") : t("register.form.continueBtn")}
                </MDButton>

                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={onBack}
                  disabled={loading}
                >
                  {t("register.form.backBtn")}
                </MDButton>
              </MDBox>

              <MDBox mt={2}>
                <MDTypography variant="caption" color="text">
                  {t("register.form.stripeNote")}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
}
